import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";

// Dashboard sub-pages
import MonBusiness from "./pages/MonBusiness";
import PortageClients from "./pages/PortageClients";
import VoixGrafton from "./pages/VoixGrafton";
import SocialDashboard from "./pages/SocialDashboard";
import SalesDashboard from "./pages/SalesDashboard";
import BriefingDashboard from "./pages/BriefingDashboard";
import TreasuryDashboard from "./pages/TreasuryDashboard";
import AccountingDashboard from "./pages/AccountingDashboard";
import BusinessPlanDashboard from "./pages/BusinessPlanDashboard";
import MyWebsitePage from "./pages/MyWebsitePage";
import ReferralPage from "./pages/ReferralPage";
import BusinessProfilePage from "./pages/BusinessProfilePage";
import CompetencesPage from "./pages/CompetencesPage";
import MyActionPlanPage from "./pages/MyActionPlanPage";
import FinancementDashboard from "./pages/FinancementDashboard";
import SettingsPage from "./pages/SettingsPage";
import AgendaDashboard from "./pages/AgendaDashboard";
import MarketingDashboard from "./pages/MarketingDashboard";
import EmailDashboard from "./pages/EmailDashboard";
import AdminNav from "./pages/AdminNav";
import CallsDashboard from "./pages/CallsDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CommunicationDashboard from "./pages/CommunicationDashboard";
import SalesFunnelDashboard from "./pages/SalesFunnelDashboard";
import CRMDashboard from "./pages/CRMDashboard";
import RHDashboard from "./pages/RHDashboard";
import SecurityPage from "./pages/SecurityPage";
import OffersPage from "./pages/OffersPage";
import NanoCorp from "./pages/NanoCorp";
import GraftonAutonomePage from "./pages/GraftonAutonomePage";
import LeverDesFondsPage from "./pages/LeverDesFondsPage";
import ClosersPage from "./pages/ClosersPage";
import LeadHuntersPage from "./pages/LeadHuntersPage";
import CoworkPage from "./pages/CoworkPage";
import Laboratoire from "./pages/Laboratoire";
import EspacePhysique from "./pages/EspacePhysique";
import { useParams } from "react-router-dom";
import { useEffect as useEffectPlan } from "react";

function PlanRedirect() {
  const { token } = useParams();
  useEffectPlan(() => {
    if (token) {
      // Open the in-app plan tab directly; the tab reads plans from API and lists the pending one
      window.location.href = `/ventes?tab=action-plan&plan_token=${token}`;
    }
  }, [token]);
  return null;
}
import CompanyCreationDashboard from "./pages/CompanyCreationDashboard";
import PartnersDashboard from "./pages/PartnersDashboard";
import TendersDashboard from "./pages/TendersDashboard";
import PaymentDashboard from "./pages/PaymentDashboard";
import ConnexionsApiPage from "./pages/ConnexionsApiPage";
import LlmUsageDashboard from "./pages/LlmUsageDashboard";
import { ActivityTracker } from "./components/ActivityTracker";
import GlobalVoice from "./components/GlobalVoice";
import SavoirFaire from "./pages/SavoirFaire";
import MonPlan from "./pages/MonPlan";
import Pilotage from "./pages/Pilotage";
import Affiliation from "./pages/Affiliation";
import Validations from "./pages/Validations";
import Maison from "./pages/Maison";
import LiensSuivis from "./pages/LiensSuivis";
import GalerieModeles from "./pages/GalerieModeles";
import ApercuModele from "./pages/ApercuModele";
import Atelier from "./pages/Atelier";
import SitePublic from "./pages/SitePublic";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PublicVisitorTracker } from "./components/PublicVisitorTracker";
import LiveVisitorsPage from "./pages/LiveVisitorsPage";
import TeamSettingsPage from "./pages/TeamSettingsPage";

// Public pages
import TarifsPage from "./pages/public/TarifsPage";
import ContactPage from "./pages/public/ContactPage";
import SolutionImmobilierPage from "./pages/public/SolutionImmobilierPage";
import SolutionLibrairiesPage from "./pages/public/SolutionLibrairiesPage";
import SolutionReprisePage from "./pages/public/SolutionReprisePage";
import SolutionFinancementPage from "./pages/public/SolutionFinancementPage";
import VisionPage from "./pages/public/VisionPage";
import CrowdfundingPage from "./pages/public/CrowdfundingPage";
import SeminairePage from "./pages/public/SeminairePage";
import AteliersPage from "./pages/public/AteliersPage";
import RecrutementPage from "./pages/public/RecrutementPage";
import DiagnosticPage from "./pages/public/DiagnosticPage";
import BourseIdeesPage from "./pages/public/BourseIdeesPage";
import PaymentCallback from "./pages/public/PaymentCallback";

const queryClient = new QueryClient();

const DOMAINE_SITES = ".graftonpartenaire.fr";
const sousDomaine = window.location.hostname.endsWith(DOMAINE_SITES) ? window.location.hostname.slice(0, -DOMAINE_SITES.length) : "";

const App = () => sousDomaine ? <SitePublic slug={sousDomaine} /> : (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <AuthProvider>
          <ActivityTracker />
          <GlobalVoice />
          <PublicVisitorTracker />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/mon-business" element={<MonBusiness />} />
            <Route path="/portage-clients" element={<PortageClients />} />
            <Route path="/voix" element={<VoixGrafton />} />
            <Route path="/savoir-faire" element={<SavoirFaire />} />
            <Route path="/mon-plan" element={<MonPlan />} />
            <Route path="/pilotage" element={<Pilotage />} />
            <Route path="/liens-suivis" element={<LiensSuivis />} />
            <Route path="/affiliation" element={<Affiliation />} />
            <Route path="/validations" element={<Validations />} />
            <Route path="/maison" element={<Maison />} />
            <Route path="/modeles" element={<GalerieModeles />} />
            <Route path="/modeles/apercu/:id" element={<ApercuModele />} />
            <Route path="/atelier" element={<Atelier />} />
            <Route path="/s/:slug" element={<SitePublic />} />
            <Route path="/social" element={<SocialDashboard />} />
            <Route path="/ventes" element={<SalesDashboard />} />
            <Route path="/briefing" element={<BriefingDashboard />} />
            <Route path="/tresorerie" element={<TreasuryDashboard />} />
            <Route path="/comptabilite" element={<AccountingDashboard />} />
            <Route path="/business-plan" element={<BusinessPlanDashboard />} />
            <Route path="/my-website" element={<MyWebsitePage />} />
            <Route path="/parrainage" element={<ReferralPage />} />
            <Route path="/adn-grafton" element={<BusinessProfilePage />} />
            <Route path="/competences" element={<CompetencesPage />} />
            <Route path="/my-action-plan" element={<MyActionPlanPage />} />
            <Route path="/grafton-autonome" element={<GraftonAutonomePage />} />
            <Route path="/lever-des-fonds" element={<LeverDesFondsPage />} />
            <Route path="/closers" element={<ClosersPage />} />
            <Route path="/lead-hunters" element={<LeadHuntersPage />} />
            <Route path="/cowork" element={<CoworkPage />} />
            <Route path="/laboratoire" element={<Laboratoire />} />
            <Route path="/laboratoire/espace-physique" element={<EspacePhysique />} />
            <Route
              path="/plan/:token"
              element={<PlanRedirect />}
            />
            <Route path="/financement" element={<FinancementDashboard />} />
            <Route path="/parametres" element={<SettingsPage />} />
            <Route path="/agenda" element={<AgendaDashboard />} />
            <Route path="/marketing" element={<MarketingDashboard />} />
            <Route path="/emails" element={<EmailDashboard />} />
            <Route path="/admin" element={<AdminNav />} />
            <Route path="/appels" element={<CallsDashboard />} />
            <Route path="/administratif" element={<AdminDashboard />} />
            <Route path="/communication" element={<CommunicationDashboard />} />
            <Route path="/tunnel-vente" element={<SalesFunnelDashboard />} />
            <Route path="/crm" element={<CRMDashboard />} />
            <Route path="/rh" element={<RHDashboard />} />
            <Route path="/securite" element={<SecurityPage />} />
            <Route path="/offres" element={<OffersPage />} />
            <Route path="/nanocorp" element={<NanoCorp />} />
            <Route path="/creation-entreprise" element={<CompanyCreationDashboard />} />
            <Route path="/partenaires" element={<PartnersDashboard />} />
            <Route path="/marches-publics" element={<TendersDashboard />} />
            <Route path="/visiteurs-live" element={<LiveVisitorsPage />} />
            <Route path="/equipe" element={<TeamSettingsPage />} />
            <Route path="/paiement" element={<PaymentDashboard />} />
            <Route path="/connexions-api" element={<ConnexionsApiPage />} />
            <Route path="/usage" element={<LlmUsageDashboard />} />
            {/* Public pages */}
            <Route path="/tarifs" element={<TarifsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/solutions/immobilier" element={<SolutionImmobilierPage />} />
            <Route path="/solutions/librairies" element={<SolutionLibrairiesPage />} />
            <Route path="/solutions/reprise" element={<SolutionReprisePage />} />
            <Route path="/solutions/financement" element={<SolutionFinancementPage />} />
            <Route path="/vision" element={<VisionPage />} />
            <Route path="/crowdfunding" element={<CrowdfundingPage />} />
            <Route path="/seminaire" element={<SeminairePage />} />
            <Route path="/ateliers" element={<AteliersPage />} />
            <Route path="/recrutement" element={<RecrutementPage />} />
            <Route path="/diagnostic" element={<DiagnosticPage />} />
            <Route path="/bourse-idees" element={<BourseIdeesPage />} />
            <Route path="/paiement/callback" element={<PaymentCallback />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
